// Calendar logic of the Marketing module (pure): where each piece sits, the
// visible range of each view and the cursor navigation. Grids come from the
// app calendar's pure helpers (read-only reuse).
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import {
  buildMonthGrid,
  buildWeekGrid,
  type MonthGridDay,
} from "@/components/fretum/calendar/calendarModel";

import type { ContentItem } from "./types";

export type CalendarView = "month" | "week" | "list";

export const CALENDAR_VIEWS: readonly CalendarView[] = [
  "month",
  "week",
  "list",
];

export const isCalendarView = (value: unknown): value is CalendarView =>
  CALENDAR_VIEWS.includes(value as CalendarView);

const DAY = "yyyy-MM-dd";

export const dayOf = (date: Date): string => format(date, DAY);

/** Local midnight of a YYYY-MM-DD day. */
export const parseDay = (day: string): Date => parseISO(day.slice(0, 10));

/** The timestamp that places a piece: planned date, else publication date. */
export const pieceDate = (item: ContentItem): Date | null => {
  const raw = item.scheduled_at || item.published_at;
  if (!raw) return null;
  const date = parseISO(raw);
  return isValid(date) ? date : null;
};

/** Local day (YYYY-MM-DD) of a piece, null when it has no date. */
export const pieceDay = (item: ContentItem): string | null => {
  const date = pieceDate(item);
  return date ? dayOf(date) : null;
};

const byDateThenTitle = (left: ContentItem, right: ContentItem): number =>
  (pieceDate(left)?.getTime() ?? 0) - (pieceDate(right)?.getTime() ?? 0) ||
  left.title.localeCompare(right.title);

/** Pieces with a date, grouped by local day, each day in time order. */
export const groupPiecesByDay = (
  items: readonly ContentItem[],
): Map<string, ContentItem[]> => {
  const map = new Map<string, ContentItem[]>();
  for (const item of [...items].sort(byDateThenTitle)) {
    const day = pieceDay(item);
    if (!day) continue;
    const list = map.get(day);
    if (list) list.push(item);
    else map.set(day, [item]);
  }
  return map;
};

/** Rows of 7 days shown by the month / week views. */
export const gridOf = (view: CalendarView, cursor: Date): MonthGridDay[][] =>
  view === "week" ? buildWeekGrid(cursor) : buildMonthGrid(cursor);

/** First and last visible day (inclusive) of a view. */
export const visibleRange = (
  view: CalendarView,
  cursor: Date,
): { start: string; end: string } => {
  if (view === "week")
    return {
      start: dayOf(startOfWeek(cursor, { weekStartsOn: 1 })),
      end: dayOf(endOfWeek(cursor, { weekStartsOn: 1 })),
    };
  if (view === "list")
    return {
      start: dayOf(startOfMonth(cursor)),
      end: dayOf(endOfMonth(cursor)),
    };
  const grid = buildMonthGrid(cursor);
  return { start: grid[0][0].key, end: grid[grid.length - 1][6].key };
};

/** Previous / next period of a view (a month for month and list). */
export const shiftCursor = (
  view: CalendarView,
  cursor: Date,
  step: 1 | -1,
): Date => (view === "week" ? addWeeks(cursor, step) : addMonths(cursor, step));

/** Days with pieces between `start` and `end` (inclusive), in order. */
export const listGroups = (
  byDay: Map<string, ContentItem[]>,
  start: string,
  end: string,
): { day: string; items: ContentItem[] }[] =>
  [...byDay.entries()]
    .filter(([day]) => day >= start && day <= end)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([day, items]) => ({ day, items }));

/** Default publication time of a piece created from a calendar day. */
export const DEFAULT_PUBLISH_TIME = "09:00";

/** "YYYY-MM-DDTHH:mm" (datetime-local input) of a day at 09:00. */
export const scheduledInputOf = (day: string): string =>
  `${day.slice(0, 10)}T${DEFAULT_PUBLISH_TIME}`;

/** ISO timestamp -> "YYYY-MM-DDTHH:mm" in local time ("" when empty). */
export const toLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = parseISO(iso);
  return isValid(date) ? format(date, "yyyy-MM-dd'T'HH:mm") : "";
};

/** "YYYY-MM-DDTHH:mm" local -> ISO timestamp (null when empty / invalid). */
export const fromLocalInput = (value: string): string | null => {
  if (!value.trim()) return null;
  const date = parseISO(value);
  return isValid(date) ? date.toISOString() : null;
};

/** The seven days of the week of `day`, Monday first. */
export const weekDaysOf = (day: string): string[] => {
  const monday = startOfWeek(parseDay(day), { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, index) => dayOf(addDays(monday, index)));
};
